import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NotificationStore } from '../../../stores/notification/notification.state';
import { Button, ButtonDirective } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { DatePipe, NgIf } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { HeaderComponent } from '../header/header.component';
import { PanelMenuModule } from 'primeng/panelmenu';
import { DividerModule } from 'primeng/divider';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MenuItem } from 'primeng/api';
import { UserStore } from '../../../stores/user/user.state';
import { makeUniqueStringArray } from '../../../utility/array.utility';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-notification-button',
  standalone: true,
  imports: [
    ButtonDirective,
    DialogModule,
    ListboxModule,
    NgIf,
    BadgeModule,
    HeaderComponent,
    PanelMenuModule,
    DividerModule,
    ScrollPanelModule,
    Button,
    DatePipe,
  ],
  templateUrl: './notification-button.component.html',
  styleUrl: './notification-button.component.scss',
})
export class NotificationButtonComponent implements OnDestroy {
  readonly store = inject(NotificationStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly userStore = inject(UserStore);

  messageItems: MenuItem[] = [];
  unreadCount = 0;
  displayDialog = false;

  constructor() {
    effect(() => {
      const notificationState = getState(this.store);
      const userState = getState(this.userStore);
      const activeUserState = getState(this.activeUserStore);

      if (!activeUserState.user || !notificationState.initialized) return;
      activeUserState.user.redMessages =
        activeUserState.user?.redMessages || [];
      this.unreadCount = this.store
        .asArray()
        .filter(
          message => !activeUserState.user?.redMessages.includes(message._id)
        ).length;
      this.initMenuItems();
    });
  }

  /**
   * Initializes the menu items based on the active user's messages and their status.
   *
   * Processes messages to determine their labels, icons, and associated commands,
   * creating a corresponding menu item for each message.
   *
   * @return {void} Does not return any value.
   */
  initMenuItems(): void {
    const redMessages = this.activeUserStore.user()?.redMessages || [];

    this.messageItems = this.store.publishedAsArray().map(item => {
      return {
        label: item.title,
        styleClass:
          this.store.selectedId() === item._id ? 'p-menuitem-link-active' : '',
        icon: redMessages.includes(item._id)
          ? 'fas fa-envelope-open'
          : 'fas fa-envelope',
        command: () => {
          this.openMessage(item._id);
        },
      };
    });
  }

  /**
   * Opens a dialog by setting the displayDialog property to true, initializing menu items,
   * and selecting the latest item from the store. Marks it as read if a selected ID exists.
   * @return {void} This method does not return a value.
   */
  openDialog(): void {
    this.displayDialog = true;
    this.initMenuItems();
    this.store.selectLast();

    if (this.store.selectedId()) {
      this.markAsRead(this.store.selectedId());
    }
  }

  /**
   * Closes the currently active dialog and resets any selected data in the store.
   *
   * @return {void} Does not return a value.
   */
  closeDialog(): void {
    this.displayDialog = false;
    this.store.deselect();
  }

  /**
   * Marks a message as read by adding its ID to the user's read messages list,
   * and updates the user's information in the store.
   *
   * @param {string} id - The ID of the message to be marked as read.
   * @return {void} This method does not return a value*/
  markAsRead(id: string): void {
    const user = this.activeUserStore.user();
    const redMessages = user?.redMessages || [];
    if (user && !redMessages.includes(id)) {
      setTimeout(() => {
        redMessages.push(id);
        this.userStore.update(user);
      }, 2000);
    }
  }

  /**
   * Marks all messages as read for the active user.
   * Combines unread message IDs with previously read message IDs, ensuring uniqueness,
   * and updates the user's message read state in the store.
   *
   * @return {void} Does not return a value.
   */
  markAllAsRead(): void {
    const user = this.activeUserStore.user();
    if (user) {
      const messages = this.store.allIds().concat(user.redMessages);
      user.redMessages = makeUniqueStringArray(messages);
      user.message = { avoid: true };
      this.userStore.update(user);
    }
  }

  /**
   * Opens a message by its unique identifier and updates the store's selection.
   *
   * @param {string} id - The unique identifier for the message to be opened.
   * @return {void} This method does not return any value.
   */
  openMessage(id: string): void {
    this.store.singleSelection(id);
    this.markAsRead(id);
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Used to handle any necessary cleanup operations such as unsubscribing
   * from observables or releasing resources. In this implementation, it deselects
   * the current state from the store.
   *
   * @return {void} This method does not return any value.
   */
  ngOnDestroy(): void {
    this.store.deselect();
  }
}
